import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { axiosPath } from '../../../../routing/helpers/router';
import Translator from '../../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../../layout/components/breadcrumb/BreadcrumbHandler';
import errorHandler from '@/layout/components/error/ErrorHandler';
import ModuleComsaEnvironmentalSideBarReports from '../ModuleComsaEnvironmentalSideBarReports';
import ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalConstruction/ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard';
import AppLink from '../../../../routing/helpers/AppLink';
import ModuleComsaEnvironmentalTitleCard from '../../../../layout/screenSpecifics/Modules/ComsaEnvironmental/ModuleComsaEnvironmentalTitleCard';
import useIsMounted from '../../../../layout/components/hooks/useIsMounted';
import { AppNoInfo } from "@/layout/AppComponents";
import * as currentPageAction from "../../../../store/actions/currentPageAction";

export default function ModuleComsaEnvironmentalConstructionDesignNormalAspectsScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [data, setData] = useState();

  // > LOAD DATA //
  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "constructionDesignNormalAspects",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setData(r.data?.sheetData ?? {});
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);
  // < LOAD DATA //

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="construction"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_construction_design_normal_aspects" />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator
                file="module-comsaEnvironmental"
                name="design.normal.aspects"
              />
            </Title>
            <Subtitle className="mb-4">
              <AppLink
                path="user_module_comsaEnvironmental_construction_control_card"
                params={{ moduleBridgeHex, subModuleBridgeHex, plantHex }}
              >
                <Translator
                  file="module-comsaEnvironmental"
                  name="environmental.design.control.form"
                />
              </AppLink>
            </Subtitle>
          </div>
        </div>
        {data && (
          <>
            <ModuleComsaEnvironmentalTitleCard color="warning">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="emissions.particle.emissions"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="air.pollution"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site}
              motive={
                <Translator file="module-comsaEnvironmental" name="site" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method.and.materials"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.ventilationSystem}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="ventilation.systems.and.indoor.air.treatment"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="warning">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="emissions.combustion.gas.emission"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="air.pollution"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site2}
              motive={
                <Translator file="module-comsaEnvironmental" name="site" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod2}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method.and.materials"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.ventilationSystem2}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="ventilation.systems.and.indoor.air.treatment"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="secondary">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="noise.vibrations.noise"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="noise.pollution"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site3}
              motive={
                <Translator file="module-comsaEnvironmental" name="site" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod3}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method.and.materials"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.definitiveScreens}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="permanent.shielding"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.constructionExecutionPlan}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="work.execution.planning.schedule"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="secondary">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="noise.vibrations.vibrations"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="vibration.pollution"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site4}
              motive={
                <Translator file="module-comsaEnvironmental" name="sites" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod4}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method.and.materials"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.antivibrationDefinitiveElements}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="permanent.anti.vibration.elements"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="info">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="discharges.water.discharges"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="water.soil.pollution"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod5}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method.and.materials"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.retentionElements}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="retention.elements"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="info">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="watercourses.affections.watercourses"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="water.pollution.resource.depletion"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site5}
              motive={
                <Translator file="module-comsaEnvironmental" name="site" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod6}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method.and.materials"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.waterPurificationSystems}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="water.purification.systems"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.courseRestorationTreatment}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="channel.restoration.treatment"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="warning">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="morphology.soil.affections"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="soil.subsoil.alteration"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site6}
              motive={
                <Translator file="module-comsaEnvironmental" name="site" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.floorRestorationTreatment}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="soil.restoration.treatment"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="info">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="marine.coastal.environment.bar.alteration.water.seabed.characteristics"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="affects.water.pollution.ecosystem.alteration.species.disappearance"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site7}
              motive={
                <Translator file="module-comsaEnvironmental" name="site" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.constructionExecutionPlan2}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="work.execution.planning.season"
                />
              }
            />

            <ModuleComsaEnvironmentalTitleCard color="success">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="impact.vegetation.damage.removal.vegetation"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="alteration.ecosystems.disappearance.plant.species"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site8}
              motive={
                <Translator file="module-comsaEnvironmental" name="site" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.constructiveMethod}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.infrastructurePermeability}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="permeability.infrastructure"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.areaVegetationRegeneration}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="vegetation.regeneration.area"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="danger">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="affectation.fauna.bar.disturbance.wildlife"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="alteration.ecosystems.disappearance.animal.species"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site9}
              motive={
                <Translator file="module-comsaEnvironmental" name="site" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.constructiveMethod2}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.infrastructurePermeability2}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="permeability.infrastructure"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.areaVegetationRegeneration2}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="vegetation.regeneration.of.the.area"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="success">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="natural.resources.consumption.raw.materials.water.fuel.electricity"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="depletion.resources"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod7}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method.and.materials"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="secondary">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="waste.bar.urban.waste"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="visual.impact.land.occupation.water.pollution.soil.pollution"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod8}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="materials.construction.method"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="secondary">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="waste.bar.inert.waste"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="visual.impact.land.occupation.water.pollution.soil.pollution"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod9}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="materials.construction.method"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="secondary">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="waste.bar.non.hazardous.waste"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="visual.impact.land.occupation.water.pollution.soil.pollution"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod10}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="materials.construction.method"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="secondary">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="waste.bar.hazardous.waste"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="visual.impact.land.occupation.water.pollution.soil.pollution"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod11}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="materials.construction.method"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="order.bar.cleanliness"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="visual.impact.soil.alteration"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.materialConstructiveMethod12}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="construction.method.and.materials"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="warning">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="landscape.landscape.alteration"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="visual.impact"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site10}
              motive={
                <Translator file="module-comsaEnvironmental" name="site" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.correctiveMeasures}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="corrective.measures"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color="primary">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="heritage.bar.historical.artistic"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="impact.on.heritage"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.site11}
              motive={
                <Translator file="module-comsaEnvironmental" name="site" />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.heritageProtection}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="heritage.protection"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="socioeconomic.impact.bar.economic.activity"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="job.generation.economic.activity.decrease"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.employmentGeneration}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="job.creation"
                />
              }
            />

            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.economicActivityIncrease}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="increase.economic.activity"
                />
              }
            />

            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.economicActivityDecrease}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="decrease.economic.activity"
                />
              }
            />

            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.expropriations}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="expropriations"
                />
              }
            />
            <ModuleComsaEnvironmentalTitleCard color={6} className="mb-2">
              <h6 className="mb-0">
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.aspect"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="socioeconomic.impact.bar.effects.on.mobility"
                  />
                </span>{" "}
                /{" "}
                <span
                  title={ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="environmental.impact"
                    />
                  )}
                >
                  <Translator
                    file="module-comsaEnvironmental"
                    name="effects.mobility"
                  />
                </span>
              </h6>
            </ModuleComsaEnvironmentalTitleCard>
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.wheeledyTrafficAlteration}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="road.traffic.alterations"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.pedestrianAlteration}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="alterations.pedestrians"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.railwayTrafficAlteration}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="rail.traffic.alterations"
                />
              }
            />
            <ModuleComsaEnvironmentalConstructionDesignNormalAspectsCard
              data={data.constructionExecutionInterruptions}
              motive={
                <Translator
                  file="module-comsaEnvironmental"
                  name="closures.diversions.during.execution.work"
                />
              }
            />
          </>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const Subtitle = styled.h3`
  text-align: center;
  font-size: 18px;
`;
